import { useCallback, useEffect, useRef, useState } from 'react';
import type { OnScrollArgs, OnScrollData, UseOnScrollProps } from './useOnScroll';
import type { Connection } from './types/paginationTypes';

export type NeedMoreRecordsArg = {
  scrollOffset: number;
  windowHeight: number;
  defaultItemHeight: number;
  /** Actual content height for lists that do not use a virtualizer. */
  scrollHeight?: number;
  areParamsReallyChanged?: boolean;
};

export type OnVirtualScrollProps = Pick<NeedMoreRecordsArg, 'windowHeight' | 'defaultItemHeight' | 'scrollHeight'>;

export interface DefaultNeedsMoreRecordsProps extends NeedMoreRecordsArg {
  dataLength: number;
}

export interface UseOnVirtualScrollProps<T extends Connection | OnScrollData>
  extends Omit<UseOnScrollProps<T>, 'needMoreRecords'> {
  needMoreRecords: (args: NeedMoreRecordsArg | null) => boolean;
  enabled?: boolean;
  /** Reset pagination attempts when the server query changes, without remounting the list. */
  queryKey?: string;
  onFetchingChange?: (fetching: boolean) => void;
}

export function defaultNeedsMoreRecords(props: DefaultNeedsMoreRecordsProps | null) {
  if (!props) return false;
  const { scrollOffset, windowHeight, defaultItemHeight, scrollHeight, areParamsReallyChanged, dataLength } = props;

  if (scrollHeight !== undefined) {
    return windowHeight > 0 && scrollHeight - scrollOffset - windowHeight <= windowHeight;
  }

  // Virtualizers report an initial scroll offset before the user scrolls.
  if (!areParamsReallyChanged) return false;
  if (!dataLength) return true;

  const totalVisibleScrolledPast = Math.ceil(scrollOffset / Math.max(defaultItemHeight, 64));
  const itemsVisibleOnPage = Math.ceil(windowHeight / Math.max(defaultItemHeight, 64));
  return totalVisibleScrolledPast + itemsVisibleOnPage * 2 >= dataLength;
}

export const useOnVirtualScroll = <T extends Connection | OnScrollData>({
  needMoreRecords,
  data,
  fetchMoreCallback,
  onError,
  enabled = true,
  queryKey = '',
  onFetchingChange,
}: UseOnVirtualScrollProps<T>): ((args: OnScrollArgs, props: OnVirtualScrollProps) => void) => {
  const [fetching, setFetching] = useState(false);
  const fetchingRef = useRef(false);
  const [lastScrollParams, setLastScrollParams] = useState<(NeedMoreRecordsArg & { queryKey: string }) | null>(null);
  const lastRequest = useRef<{ queryKey: string; cursor: string; scrollOffset: number; failed: boolean } | null>(null);
  const hasMoreRecords = 'hasNextPage' in data ? data.hasNextPage : (data as Connection)?.pageInfo?.hasNextPage;
  const cursor = ('endCursor' in data ? data.endCursor : (data as Connection)?.pageInfo?.endCursor) ?? '';

  useEffect(() => {
    if (lastRequest.current?.queryKey !== queryKey) lastRequest.current = null;
  }, [queryKey]);

  useEffect(() => {
    onFetchingChange?.(fetching);
  }, [fetching, onFetchingChange]);

  useEffect(() => {
    if (!enabled || fetchingRef.current || !hasMoreRecords || lastScrollParams?.queryKey !== queryKey) return;
    if (!needMoreRecords(lastScrollParams)) return;

    const previous = lastRequest.current;
    // Do not repeat a completed cursor or retry a failure until the user scrolls forward.
    if (previous?.queryKey === queryKey && previous.cursor === cursor) {
      if (!previous.failed || lastScrollParams.scrollOffset <= previous.scrollOffset) return;
    }

    const request = { queryKey, cursor, scrollOffset: lastScrollParams.scrollOffset, failed: false };
    lastRequest.current = request;
    // Effects can replay in StrictMode. Acquire the guard before starting the request.
    fetchingRef.current = true;
    setFetching(true);
    Promise.resolve()
      .then(() => fetchMoreCallback(cursor))
      .catch((error) => {
        request.failed = true;
        onError?.(error);
      })
      .finally(() => {
        fetchingRef.current = false;
        setFetching(false);
      });
  }, [
    enabled,
    fetching,
    hasMoreRecords,
    cursor,
    lastScrollParams,
    queryKey,
    fetchMoreCallback,
    needMoreRecords,
    onError,
  ]);

  return useCallback(
    (args: OnScrollArgs, params: OnVirtualScrollProps) => {
      if (args.scrollDirection !== 'forward') return;
      setLastScrollParams((current) => {
        const updated = { ...params, scrollOffset: args.scrollOffset, queryKey };
        const changed =
          current?.queryKey !== queryKey ||
          current.scrollOffset !== updated.scrollOffset ||
          current.windowHeight !== updated.windowHeight ||
          current.defaultItemHeight !== updated.defaultItemHeight ||
          current.scrollHeight !== updated.scrollHeight;
        if (current && !changed) return current;
        return { ...updated, areParamsReallyChanged: current !== null && changed };
      });
    },
    [queryKey],
  );
};
